import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  Helper,
  Input,
  Label,
  Required,
} from "../components/shared/InputField";
import { VStack, HStack, Error } from "../components/utils";
import { SpocDetailModel, SpocType } from "../types/OnBoardDataModel";
import { PopupHeader } from "../components/Headings";
import {
  ButtonSize,
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../pages/quickRound/CTAButtonComponents";
import { useAddSpocDetails, useUpdateSpocDetails } from "../queries/spocDetails";

const initialSpocData: SpocDetailModel = {
  type: SpocType.SPOC,
  name: "",
  email: "",
  phone: "",
};

const spocValidationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  type: Yup.string()
    .oneOf(Object.values(SpocType), "Type must be KMP or SPOC")
    .required("Type is required"),
  email: Yup.string()
    .email("Enter a valid Email")
    .required("Email is required"),
  phone: Yup.string(),
});

type AddEditSpocProps = {
  spoc?: SpocDetailModel;
  onSecondaryAction?: () => void;
  mode?: "Add" | "Edit";
};

const AddEditSpoc = ({
  spoc,
  onSecondaryAction = () => {},
  mode,
}: AddEditSpocProps) => {
  const [loading, setLoading] = useState(false);
  const { mutate: addSpocDetailsMutation } = useAddSpocDetails();
  const { mutate: updateSpocDetailsMutation } = useUpdateSpocDetails();

  const formik = useFormik<SpocDetailModel>({
    initialValues: spoc || initialSpocData,
    validationSchema: spocValidationSchema,
    onSubmit: (data) => {
      setLoading(true);
      const onSettled = {
        onSuccess: () => {
          toast("SPOC Saved Successfully!", {
            type: "success",
            autoClose: 2000,
          });
          setLoading(false);
          onSecondaryAction();
        },
        onError: (err: any) => {
          toast(
            err?.response?.data?.errorMessage || "Failed to save SPOC Details",
            {
              type: "error",
              autoClose: 2000,
            }
          );
          setLoading(false);
        },
      };
      if (mode === "Edit") {
        updateSpocDetailsMutation(data, onSettled);
      } else {
        addSpocDetailsMutation(data, onSettled);
      }
    },
  });

  const { touched, errors, getFieldProps, values } = formik;
  const spocTypeLabel = values.type === SpocType.KMP ? "KMP" : "SPOC";

  const handleClose = () => {
    onSecondaryAction();
  };

  return (
    <div className="transition ease-in duration-2000 opacity-100 enter:opacity-0">
      <PopupHeader
        text={mode === "Edit" ? `Edit ${spocTypeLabel} Details` : `Add ${spocTypeLabel} Details`}
        onClick={() => {
          handleClose();
        }}
      />
      <VStack className="w-full gap-8 px-14 py-8 pb-14">
        <HStack className="gap-8">
          <div className="flex-1">
            <Label>
              {spocTypeLabel} Name
              <Required />
            </Label>
            <Input
              type="text"
              error={!!(touched.name && errors.name)}
              placeholder={`Enter ${spocTypeLabel} Name`}
              className="w-96"
              {...getFieldProps("name")}
            />
            <Helper>Enter the name of the {spocTypeLabel}</Helper>
            {touched.name && errors.name && <Error text={errors.name} />}
          </div>
        </HStack>
        <HStack className="gap-8">
          <div className="flex-1">
            <Label>
              {spocTypeLabel} Email ID
              <Required />
            </Label>
            <Input
              type="email"
              error={!!(touched.email && errors.email)}
              placeholder="Eg: example@domain.com"
              className="w-96"
              {...getFieldProps("email")}
            />
            {touched.email && errors.email && <Error text={errors.email} />}
          </div>
          <div className="flex-1">
            <Label>Phone Number</Label>
            <Input
              type="text"
              error={!!(touched.phone && errors.phone)}
              placeholder="Eg: 9876543210"
              className="w-96"
              {...getFieldProps("phone")}
            />
            {touched.phone && errors.phone && <Error text={errors.phone} />}
          </div>
        </HStack>
        <HStack className="justify-between h-8">
          <SecondaryCTAButton
            event-name="Cancel SPOC Modal"
            buttonSize={ButtonSize.MEDIUM}
            onClick={() => {
              handleClose();
            }}
          >
            Cancel
          </SecondaryCTAButton>
          <PrimaryCTAButton
            event-name="Save SPOC Modal"
            buttonSize={ButtonSize.SMALL}
            onClick={() => {
              formik.handleSubmit();
            }}
            loading={loading}
            disabled={loading}
          >
            Save
          </PrimaryCTAButton>
        </HStack>
      </VStack>
    </div>
  );
};

export default AddEditSpoc;
